import React, { useEffect, useMemo, useRef, useState } from "react"
import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import ModernMUITable from "../../Components/ModernMUITable"
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme } from "../../Moberg"
import { useBackendLinksProvider } from "../../Providers/BackendLinksProvider"
import { useEnvironmentVariablesProvider, Deployment } from "../../Providers/EnvironmentVariablesProvider"
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider"
import { localizeDate } from "../../Computation/utilFunctions"
import { hasReadableRecording, hasRecording, patientsEndpoint } from "./claraPatients"
import { useDialogFocus } from "./dialogFocus"
import styles from "./Clara.module.css"

const TABS = ["admitted", "discharged", "all"]

// Matches the tabs on the Data Visualize page, so the same control looks the same in
// both places.
const TAB_STYLES = {
	textTransform: "none",
	fontSize: "14px",
	minHeight: "38px",
	padding: "6px 14px",
	borderRadius: "4px",
	color: "var(--text-secondary-color)",
	"&.Mui-selected": { color: "var(--color-accent-interactive)" },
}

// Renders the same duration string Data Visualize shows ("2 days 4 hrs 9 mins").
function formatDuration(milliseconds) {
	if (!milliseconds) {
		return "Unknown"
	}
	const seconds = milliseconds / 1000
	const days = Math.floor(seconds / (3600 * 24))
	const hours = Math.floor((seconds - days * 3600 * 24) / 3600)
	const minutes = Math.floor((seconds - days * 3600 * 24 - hours * 3600) / 60)
	if (days > 0) {
		return `${days} days ${hours} hrs ${minutes} mins`
	}
	if (hours > 0) {
		return `${hours} hrs ${minutes} mins`
	}
	return `${minutes} mins`
}

function formatTime(value, timezone) {
	if (!value) {
		return "Unknown"
	}
	try {
		return localizeDate(new Date(value), timezone?.[0])
	} catch (error) {
		return "Unknown"
	}
}

/* The patient picker. Same table as Data Visualize (ModernMUITable), driven the same
   way it drives it: SERVER side.

   It used to be handed a prefetched array via `tableData`, which silently puts the grid
   in client mode. Two things followed from that one prop, and both were reported from
   UTSW on the same afternoon:

     - the toolbar search became a MUI quick filter over the loaded rows, so it could
       only find patients inside the page Clara happened to have fetched;
     - sorting compared whatever the column rendered, and the date columns rendered
       "31 Jul 2026 - 8:57 AM", so Started and Ended sorted alphabetically. July above
       January, 2026 next to 2025, and the year never got a vote.

   In server mode the search and the sort go to Postgres against every patient, which is
   why the same table on the Data Visualize page never had either problem. The columns
   below now also keep raw values in valueGetter and format in renderCell, so a sort is
   a sort on a timestamp wherever it happens.
*/
export function ClaraPatientModal({ open, selectedKey, onSelect, onSkip, onClose }) {
	const dialogRef = useRef(null)
	useDialogFocus(open, dialogRef, "input[type='text'], input:not([type])")
	const { LINKS } = useBackendLinksProvider()
	const { deployment } = useEnvironmentVariablesProvider()
	const { selectedWorkspace } = useWorkspacesProvider()
	const isClinical = deployment === Deployment.CLINICAL
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })

	/* Admitted, Discharged, All: the same three Data Visualize offers, defaulting to
	   Admitted for the same reason, that at a bedside the patient you want is almost
	   always one who is still here.

	   Both list endpoints filter on admission_status AND apply the same filter to their
	   count query, so paging stays honest inside a tab. Search is a separate parameter, so
	   the two compose: a term typed on Admitted searches admitted patients only.

	   The page resets whenever the tab changes. Without that you can be on page 4 of the
	   admitted list, switch to a shorter one, and be shown an empty table for a tab that
	   has patients in it. The search TERM is deliberately kept, because "find this patient,
	   they must be discharged then" is the reason someone changes tab at all. */
	const [admissionStatus, setAdmissionStatus] = useState("admitted")
	const changeTab = next => {
		setAdmissionStatus(next)
		setPaginationModel(model => ({ page: 0, pageSize: model.pageSize }))
	}

	useEffect(() => {
		if (!open) {
			return
		}
		const onKeyDown = event => {
			if (event.key === "Escape") {
				onClose?.()
			}
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	}, [open, onClose])

	const choose = patient => {
		// The whole row, not just the key: the page can no longer look a patient up in a
		// list it has not fetched, and this is the row the server just gave us.
		onSelect?.(patient)
		onClose?.()
	}

	const columns = useMemo(
		() => [
			{
				field: "select",
				headerName: "",
				width: 90,
				minWidth: 90,
				maxWidth: 90,
				headerAlign: "center",
				align: "center",
				sortable: false,
				filterable: false,
				hideable: false,
				// The server returns every patient, including ones with no archive and ones
				// whose archive files are not on this server. Clara can watch neither, so
				// the row is shown and the action is not: a greyed row with a reason beats
				// a Select button that appears to work and then answers "I don't have that
				// data" to every question, which is exactly what used to happen.
				renderCell: params =>
					hasReadableRecording(params.row) ? (
						<MobergButton
							variant={MobergButtonVariant.CLEAR}
							theme={MobergTheme.BLUE}
							shape={MobergButtonShape.RECTANGLE}
							onClick={() => choose(params.row)}
							style={{ marginTop: "5px", height: "40px" }}
						>
							{String(params.row.patient_key) === String(selectedKey) ? "Watching" : "Select"}
						</MobergButton>
					) : (
						<span
							className={styles.patientModalNoRecording}
							title={
								hasRecording(params.row)
									? "The files for this patient's recordings could not be found on the server."
									: "This patient has no recording to review."
							}
						>
							{hasRecording(params.row) ? "Files missing" : "No data"}
						</span>
					),
			},
			{
				field: "patient_id",
				headerName: "Patient ID",
				minWidth: 180,
				flex: 0.4,
				visible: true,
				valueGetter: (value, row) => row.patient_id || `Unknown-${row.patient_key}`,
			},
			{
				field: "patient_name",
				headerName: "Name",
				minWidth: 160,
				flex: 0.4,
				visible: true,
				// Only the clinical list endpoint can sort by name; the other one has no
				// patient_name in its sort map, and an unrecognised field there means NO
				// order by is applied at all. So the header would not sort the list, it
				// would reshuffle it into whatever order Postgres felt like returning.
				sortable: isClinical,
				valueGetter: (value, row) => (row.patient_name?.trim() ? row.patient_name : "Unknown"),
			},
			// The three columns below sort on the RAW value and format for display. Reading
			// the formatted string back out of valueGetter is what made "31 Jul 2026" sort
			// above "31 Jan 2026", and "2 mins" above "3 days 7 hrs".
			{
				field: "start_time",
				headerName: "Started",
				minWidth: 170,
				flex: 0.4,
				visible: true,
				valueGetter: (value, row) => (row.start_time ? new Date(row.start_time).getTime() : null),
				renderCell: params => formatTime(params.row.start_time, params.row.timezone),
			},
			{
				field: "end_time",
				headerName: "Ended",
				minWidth: 170,
				flex: 0.4,
				visible: true,
				valueGetter: (value, row) => (row.end_time ? new Date(row.end_time).getTime() : null),
				renderCell: params => formatTime(params.row.end_time, params.row.timezone),
			},
			{
				field: "total_monitoring_time",
				headerName: "Duration",
				minWidth: 150,
				flex: 0.3,
				visible: true,
				valueGetter: (value, row) => row.total_monitoring_time ?? null,
				renderCell: params => formatDuration(params.row.total_monitoring_time),
			},
		],
		// choose is stable enough here: it only closes and hands the row back up.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedKey, isClinical, onSelect, onClose]
	)

	const tableToolbarProps = {
		createButton: { isShow: false },
		deleteButton: { isShow: false },
		filterButton: { isShow: false },
		exportButton: { isShow: false },
		columnButton: { isShow: false },
		searchInput: { isShow: true, placeholder: "Search by patient ID or name" },
		mainTable: true,
	}

	if (!open) {
		return null
	}

	return (
		<div className={styles.patientModalOverlay} onClick={() => onClose?.()}>
			<div ref={dialogRef} className={styles.patientModal} onClick={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="Select a patient" tabIndex={-1}>
				<div className={styles.patientModalHeader}>
					<div>
						<div className={styles.patientModalTitle}>Select a patient</div>
						<div className={styles.patientModalSubtitle}>Clara watches the most recent archive for whoever you pick.</div>
					</div>
					<div className={styles.patientModalActions}>
						{/* A chat does not have to be about a patient. Document questions ("who may
						    access an Ommaya reservoir") need none, and forcing a pick before them
						    would make the whole documents half of Clara worse. Only offered on a
						    chat that has no patient yet; there is nothing to skip once it has one. */}
						{onSkip && (
							<button type="button" className={styles.patientModalSkip} onClick={() => onSkip()}>
								Skip, ask about the documents
							</button>
						)}
						<button type="button" className={styles.patientModalClose} onClick={() => onClose?.()} aria-label="Close">
							×
						</button>
					</div>
				</div>

				{/* Only clinical deployments track admission, which is why Data Visualize gates
				    its own tabs the same way. */}
				{isClinical && (
					<div className={styles.patientModalTabs}>
						<Tabs
							value={TABS.indexOf(admissionStatus)}
							onChange={(event, index) => changeTab(TABS[index])}
							aria-label="Filter patients by admission status"
							sx={{ minHeight: "38px", "& .MuiTabs-indicator": { backgroundColor: "var(--color-accent-interactive)" } }}
						>
							<Tab label="Admitted" disableRipple sx={TAB_STYLES} />
							<Tab label="Discharged" disableRipple sx={TAB_STYLES} />
							<Tab label="All" disableRipple sx={TAB_STYLES} />
						</Tabs>
					</div>
				)}

				<div className={styles.patientModalTable}>
					<ModernMUITable
						queryKey="clara-select-patient"
						endpoint={patientsEndpoint(LINKS, isClinical)}
						body={
							isClinical
								? { workspace_name: selectedWorkspace, admission_status: admissionStatus }
								: { workspace_name: selectedWorkspace }
						}
						optionalParams={{}}
						supportsPageSize={true}
						paginationMode="server"
						sortingMode="server"
						defaultSortModel={[{ field: "patient_id", sort: "asc" }]}
						columns={columns}
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
						tableToolbarProps={tableToolbarProps}
						pageSizeOptions={[10, 25, 50]}
						handleOnRowClick={row => hasReadableRecording(row) && choose(row)}
						isRowSelectable={() => false}
						isCheckboxSelection={false}
						checkboxSelection={false}
						height="100%"
					/>
				</div>
			</div>
		</div>
	)
}
